import { MoreHorizontal, Calendar, Clock, Folder, Archive, Copy, Trash2, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";

export interface Project {
  id: string;
  name: string;
  description?: string;
  thumbnail?: string;
  createdAt: Date;
  updatedAt: Date;
  materialsCount: number;
  status: "active" | "archived" | "draft";
}

interface ProjectCardProps {
  project: Project;
  onClick?: (project: Project) => void;
  onRename?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onArchive?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export function ProjectCard({
  project,
  onClick,
  onRename,
  onDuplicate,
  onArchive,
  onDelete,
}: ProjectCardProps) {
  const statusColors = {
    active: "bg-green-500/10 text-green-700 border-green-200",
    archived: "bg-muted text-muted-foreground border-border",
    draft: "bg-amber-500/10 text-amber-700 border-amber-200",
  };

  return (
    <div
      className="group relative overflow-hidden rounded-xl border border-border bg-card shadow-card transition-all duration-500 ease-out hover:shadow-card-hover hover:-translate-y-2 hover:border-primary/20 cursor-pointer"
      onClick={() => onClick?.(project)}
    >
      {/* Thumbnail */}
      <div className="aspect-video overflow-hidden bg-muted relative">
        {project.thumbnail ? (
          <img
            src={project.thumbnail}
            alt={project.name}
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center transition-all duration-500 group-hover:bg-primary/5">
            <Folder className="h-12 w-12 text-muted-foreground/40 transition-all duration-500 group-hover:scale-110 group-hover:text-primary/40" />
          </div>
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/30 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      </div>

      {/* Content */}
      <div className="p-4 transition-all duration-300 group-hover:bg-muted/20">
        <div className="mb-2 flex items-start justify-between gap-2">
          <h3 className="font-serif text-lg font-medium text-foreground line-clamp-1 transition-colors duration-300 group-hover:text-primary">
            {project.name}
          </h3>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 opacity-0 transition-all duration-300 group-hover:opacity-100 hover:bg-primary/10 hover:scale-110 active:scale-95"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-popover animate-in fade-in-0 zoom-in-95">
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); onRename?.(project.id); }}
                className="transition-colors duration-200 hover:bg-primary/10"
              >
                <Edit className="mr-2 h-4 w-4" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); onDuplicate?.(project.id); }}
                className="transition-colors duration-200 hover:bg-primary/10"
              >
                <Copy className="mr-2 h-4 w-4" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); onArchive?.(project.id); }}
                className="transition-colors duration-200 hover:bg-primary/10"
              >
                <Archive className="mr-2 h-4 w-4" />
                Archive
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => { e.stopPropagation(); onDelete?.(project.id); }}
                className="text-destructive focus:text-destructive transition-colors duration-200"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {project.description && (
          <p className="mb-3 text-sm text-muted-foreground line-clamp-2 transition-colors duration-300">
            {project.description}
          </p>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs text-muted-foreground transition-colors duration-300">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3 transition-transform duration-300 group-hover:scale-110" />
              {formatDistanceToNow(project.updatedAt, { addSuffix: true })}
            </span>
            <span>{project.materialsCount} materials</span>
          </div>
          <Badge variant="outline" className={cn(statusColors[project.status], "transition-all duration-300 group-hover:scale-105")}>
            {project.status}
          </Badge>
        </div>
      </div>
    </div>
  );
}
